// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo } from 'react';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import useListQuery from '@Hooks/useListQuery';
import { DashboardOutput } from 'thehive-sdk';
import client from '@Client';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THForm } from '@Molecules/THForm/THForm';
import { useLocalMessage } from '@Helpers/locale';
import { LanguageSwitcherContext } from '@Lang/LanguageSwitcherContext';
import moment from 'moment';
import useValidateForm from '@Hooks/useValidateForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useGetSettings } from '@Queries/UISettingsQueries';
import { DEFAULT_DATE_FORMAT } from '@Hooks/useFormatDate';
import { useStorageOptions } from '@Hooks/useStorageOptions';
import usePageTitle from '@Hooks/usePageTitle';

import './Options.scss';

const Options = (): ReactElement => {
    const i = useLocalMessage();
    const { language } = useContext(LanguageSwitcherContext);
    const { data: settingsData } = useGetSettings();
    const { homepage, defaultDateFormat, setOptions } = useStorageOptions();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const mom = moment().locale(language);

    const { data: dashboardList } = useListQuery<DashboardOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listDashboard' }],
                name: 'get-all-dashboards',
                sort: ['+status', '+name'],
            },
        },
        controller: client.DashboardController,
    });

    const possibleHomepages: Record<string, { label: string; route: string }> = useMemo(
        () => ({
            cases: { label: i('breadcrumb.global.cases'), route: RouteBuilder.compute('cases.default') },
            alerts: { label: i('breadcrumb.global.alerts'), route: RouteBuilder.compute('alerts.default') },
            tasks: { label: i('breadcrumb.case.tasks'), route: RouteBuilder.compute('globalTasks.default') },
        }),
        [i],
    );

    usePageTitle(i('page-title.account.options'));

    return (
        <THLayout>
            <THLayout.Content className="options__layout">
                <THForm
                    name="settings-form"
                    form={form}
                    onFinish={({ homepage, defaultDateFormat }) => {
                        setOptions({ homepage, defaultDateFormat });
                        form.resetFields();
                        setFieldsTouched(false);
                    }}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    initialValues={{
                        homepage,
                        defaultDateFormat: defaultDateFormat || settingsData?.defaultDateFormat || DEFAULT_DATE_FORMAT,
                    }}
                    className="settings-form"
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                warning={fieldsTouched}
                                onCancel={() => {
                                    form.resetFields();
                                    setFieldsTouched(false);
                                }}
                                disabled={!isValid}
                                hidden={!fieldsTouched}
                            />
                        }
                    >
                        <THForm.Item
                            name="homepage"
                            label={i('account.options.homepage.label')}
                            className="th-editable__form-item"
                            help={i('account.options.homepage.help')}
                        >
                            <THSelect>
                                <>
                                    <THSelect.OptGroup key="Default">
                                        {Object.values(possibleHomepages).map(({ label, route }) => (
                                            <THSelect.Option key={route} value={route}>
                                                {label}
                                            </THSelect.Option>
                                        ))}
                                    </THSelect.OptGroup>

                                    {!!dashboardList?.length && (
                                        <THSelect.OptGroup key="Dashboards">
                                            {dashboardList?.map(({ _id, title }) => (
                                                <THSelect.Option
                                                    key={_id}
                                                    value={RouteBuilder.compute('dashboards.details', {
                                                        dashboardId: _id,
                                                    })}
                                                >
                                                    {title}
                                                </THSelect.Option>
                                            ))}
                                        </THSelect.OptGroup>
                                    )}
                                </>
                            </THSelect>
                        </THForm.Item>
                        <THForm.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => (
                                <THForm.Item
                                    label={i('settings.form.defaultDateFormat.label')}
                                    name="defaultDateFormat"
                                    rules={[{ required: true, message: i('settings.form.defaultDateFormat.required') }]}
                                    help={
                                        <>
                                            {i('settings.form.defaultDateFormat.descriptions.currentPreview')}{' '}
                                            <strong>{mom.format(getFieldValue('defaultDateFormat'))}</strong>
                                        </>
                                    }
                                >
                                    <THInput placeholder="YYYY-MM-DD HH:mm" />
                                </THForm.Item>
                            )}
                        </THForm.Item>
                        <ul className="options__display-date">
                            {[
                                'YYYY-MM-DD HH:mm',
                                'DD/MM/YYYY HH:mm',
                                'DD.MM.YY HH:mm:ss',
                                'MM-DD-YYYY HH:mm:ssZ',
                                'ddd, MMM Do, YYYY H:mm Z',
                            ].map((format) => (
                                <li key={format} className="options__display-date__item">
                                    <THButton
                                        type="link"
                                        onClick={(): void => {
                                            form.setFieldsValue({
                                                defaultDateFormat: format,
                                            });
                                            onFormValuesChange();
                                            setFieldsTouched(true);
                                        }}
                                    >
                                        {format}
                                    </THButton>
                                    {i('settings.form.defaultDateFormat.descriptions.display')}
                                    <span className="options__display-date__date">{mom.format(format)}</span>
                                </li>
                            ))}
                        </ul>
                    </THForm.Layout>
                </THForm>
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'Options',
    },
    path: RouteBuilder.compute('account.options'),
    content: <Options />,
    space: ['admin', 'user'],
    breadcrumb: { label: 'Options' },
};
